import React, { useEffect, useMemo, useState } from "react";
import { Button, Header, Icon, Loader, Message } from "semantic-ui-react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import { getDriverDirectory, getDrivers } from "api/backendAPI";
import {
  mergeDriverDirectory,
  sortDrivers,
  validateDriverDirectoryResponse,
  validateDriverListResponse,
} from "utils/driverViewModel";

import "./Drivers.scss";

const EMPTY = "—";

function contactIcon(channel) {
  switch ((channel || "").toLowerCase()) {
    case "email": return "mail outline";
    case "phone": return "phone";
    case "whatsapp": return "whatsapp";
    default: return "address card outline";
  }
}

function uniqueContacts(contacts) {
  const seen = new Set();
  return contacts.filter(contact => {
    if (seen.has(contact.contact_id)) return false;
    seen.add(contact.contact_id);
    return true;
  });
}

function formatDate(value, language) {
  if (!value) return EMPTY;
  return new Intl.DateTimeFormat(language === "fi" ? "fi-FI" : "en-GB", { dateStyle: "short" }).format(new Date(value));
}

export function ContactRow({ contact, t, language }) {
  return (
    <div className="driverCard__contact">
      <Icon name={contactIcon(contact.channel_type)} />
      <span>
        <strong>{contact.value}</strong>
        <small>{contact.channel_type} · {contact.purpose}{contact.vehicle_ref ? ` · ${contact.vehicle_ref}` : ""}</small>
      </span>
      <span className="driverCard__contactFlags">
        {contact.restricted && <span className="driverCard__restricted">{t("drivers.restricted")}</span>}
        {contact.is_primary && <span className="driverCard__primary">{t("drivers.primary")}</span>}
        {contact.whatsapp_eligible && <span className="driverCard__primary">WhatsApp</span>}
      </span>
      <details className="driverCard__audit">
        <summary aria-label={t("drivers.contactDetails")}><Icon name="info circle" /></summary>
        <dl>
          <div><dt>{t("drivers.ownerType")}</dt><dd>{contact.owner_type}</dd></div>
          <div><dt>{t("drivers.consent")}</dt><dd>{contact.personal_use_consent === null ? t("drivers.notApplicable") : contact.personal_use_consent ? t("drivers.confirmed") : t("drivers.notConfirmed")}</dd></div>
          <div><dt>{t("drivers.consentVerified")}</dt><dd>{formatDate(contact.consent_verified_at, language)}</dd></div>
          <div><dt>{t("drivers.validFrom")}</dt><dd>{formatDate(contact.valid_from, language)}</dd></div>
          <div><dt>{t("drivers.contactId")}</dt><dd>{contact.contact_id}</dd></div>
        </dl>
      </details>
    </div>
  );
}

export function DriverCard({ driver, t, language }) {
  const contacts = uniqueContacts(driver.contacts);
  const assignments = driver.assignments || [];
  const vehicles = [...new Set(assignments.map(assignment => assignment.vehicle_ref).filter(Boolean))];
  const roles = [...new Set(assignments.map(assignment => [assignment.assignment_role, assignment.assignment_status].filter(Boolean).join(" · ")).filter(Boolean))];
  return (
    <article className="driverCard">
      <div className="driverCard__head">
        <div className="driverCard__avatar" aria-hidden="true">
          {(driver.preferred_name || driver.display_name).trim().charAt(0).toUpperCase()}
        </div>
        <div>
          <h2>{driver.display_name}</h2>
          {driver.preferred_name && driver.preferred_name !== driver.display_name && (
            <p>{t("drivers.preferredName")}: {driver.preferred_name}</p>
          )}
        </div>
        <span className={`driverCard__status driverCard__status--${driver.active ? "active" : "inactive"}`}>
          {driver.active ? t("drivers.active") : t("drivers.inactive")}
        </span>
      </div>

      <dl className="driverCard__facts">
        <div>
          <dt>{t("drivers.vehicle")}</dt>
          <dd>{vehicles.join(", ") || EMPTY}</dd>
        </div>
        <div>
          <dt>{t("drivers.assignment")}</dt>
          <dd>{roles.join(", ") || EMPTY}</dd>
        </div>
        <div>
          <dt>{t("drivers.language")}</dt>
          <dd>{driver.primary_language ? driver.primary_language.toUpperCase() : EMPTY}</dd>
        </div>
      </dl>

      <div className="driverCard__contacts">
        <h3>{t("drivers.contacts")}</h3>
        {contacts.length === 0 ? (
          <p className="driverCard__missing">{t("drivers.noContacts")}</p>
        ) : contacts.map(contact => <ContactRow contact={contact} key={contact.contact_id} language={language} t={t} />)}
      </div>
      <details className="driverCard__provenance">
        <summary>{t("drivers.provenance")}</summary>
        <dl>
          <div><dt>{t("drivers.driverId")}</dt><dd>{driver.driver_id}</dd></div>
          {assignments.length === 0 ? <div><dt>{t("drivers.assignments")}</dt><dd>{EMPTY}</dd></div> : assignments.map((assignment, index) => (
            <React.Fragment key={`${assignment.vehicle_ref || "assignment"}-${index}`}>
              <div><dt>{t("drivers.vehicle")}</dt><dd>{assignment.vehicle_ref || EMPTY}</dd></div>
              <div><dt>{t("drivers.evidenceRef")}</dt><dd>{assignment.assignment_evidence_ref || EMPTY}</dd></div>
              <div><dt>{t("drivers.validity")}</dt><dd>{formatDate(assignment.valid_from, language)} – {formatDate(assignment.valid_to, language)}</dd></div>
            </React.Fragment>
          ))}
        </dl>
      </details>
    </article>
  );
}

export function DriverDirectoryView({ drivers, loading, error, query, status, onQuery, onStatus, t, language }) {
  if (loading) return <div className="directoryState"><Loader active inline="centered" content={t("drivers.loading")} /></div>;
  if (error) return (
    <Message negative className="directoryState" data-testid="driver-directory-error">
      <Message.Header>{t("drivers.errorTitle")}</Message.Header>
      <p>{t(error === "contract" ? "drivers.contractError" : "drivers.errorBody")}</p>
    </Message>
  );

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visible = drivers.filter(driver => {
    const statusMatches = status === "all" || (status === "active" ? driver.active : !driver.active);
    const text = [driver.display_name, driver.preferred_name, driver.primary_language, ...(driver.assignments || []).map(assignment => assignment.vehicle_ref)]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase();
    return statusMatches && (!normalizedQuery || text.includes(normalizedQuery));
  });

  return (
    <>
      <section className="directoryTools" aria-label={t("drivers.filters")}>
        <label className="directoryTools__search">
          <Icon name="search" />
          <span className="srOnly">{t("drivers.search")}</span>
          <input
            type="search"
            value={query}
            onChange={event => onQuery(event.target.value)}
            placeholder={t("drivers.searchPlaceholder")}
          />
        </label>
        <div className="directoryTools__filters">
          {["all", "active", "inactive"].map(value => (
            <button
              type="button"
              className={status === value ? "is-active" : ""}
              key={value}
              onClick={() => onStatus(value)}
            >
              {t(`drivers.filters.${value}`)}
            </button>
          ))}
        </div>
      </section>
      {visible.length === 0 ? (
        <Message className="directoryState" data-testid="driver-directory-empty">
          <Message.Header>{t("drivers.emptyTitle")}</Message.Header>
          <p>{t("drivers.emptyBody")}</p>
        </Message>
      ) : (
        <section className="driverGrid" data-testid="driver-directory-list">
          {visible.map(driver => <DriverCard driver={driver} key={driver.driver_id} language={language} t={t} />)}
        </section>
      )}
    </>
  );
}

export default function Drivers() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ drivers: [], loading: true, error: null, lastLoaded: null });
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setState(current => ({ ...current, drivers: [], loading: true, error: null }));
    Promise.all([getDrivers(), getDriverDirectory()])
      .then(([contextDrivers, directory]) => {
        if (!validateDriverListResponse(contextDrivers) || !validateDriverDirectoryResponse(directory)) {
          throw Object.assign(new Error("invalid driver registry response"), { kind: "contract" });
        }
        const drivers = mergeDriverDirectory(contextDrivers, directory);
        if (!drivers) throw Object.assign(new Error("driver registry projections disagree"), { kind: "contract" });
        if (active) setState({ drivers: sortDrivers(drivers, i18n.language), loading: false, error: null, lastLoaded: new Date().toISOString() });
      })
      .catch(error => active && setState({ drivers: [], loading: false, error: error.kind || "network", lastLoaded: null }));
    return () => { active = false; };
  }, [i18n.language, reloadKey]);

  const metrics = useMemo(() => ({
    total: state.drivers.length,
    active: state.drivers.filter(driver => driver.active).length,
    assigned: state.drivers.filter(driver => driver.active && driver.assignments.some(assignment => assignment.vehicle_ref)).length,
    contacts: new Set(state.drivers.flatMap(driver => driver.contacts.map(contact => contact.contact_id))).size,
  }), [state.drivers]);

  return (
    <main className="pageShell drivers">
      <Helmet title={t("drivers.title")} />
      <header className="pageHeader">
        <div>
          <p className="pageHeader__eyebrow">FleetAdmin TMS · {t("navbar.readOnly")}</p>
          <Header as="h1">{t("drivers.title")}</Header>
          <p className="pageHeader__lead">{t("drivers.lead")}</p>
        </div>
        <Button className="refreshButton" onClick={() => setReloadKey(key => key + 1)} icon="refresh" content={t("drivers.refresh")} />
      </header>

      {!state.loading && !state.error && (
        <section className="metricStrip" aria-label={t("drivers.summary")}>
          {Object.entries(metrics).map(([name, value]) => (
            <div className="metricStrip__item" key={name}>
              <span className="metricStrip__label">{t(`drivers.metrics.${name}`)}</span>
              <span className="metricStrip__value">{value}</span>
            </div>
          ))}
        </section>
      )}

      {state.lastLoaded && <p className="drivers__loadedAt">{t("drivers.lastLoaded")}: {new Intl.DateTimeFormat(i18n.language === "fi" ? "fi-FI" : "en-GB", { dateStyle: "short", timeStyle: "short" }).format(new Date(state.lastLoaded))}</p>}
      <DriverDirectoryView
        {...state}
        query={query}
        status={status}
        onQuery={setQuery}
        onStatus={setStatus}
        t={t}
        language={i18n.language}
      />
    </main>
  );
}
